import { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

// ── ErrorBanner ───────────────────────────────────────────────────────────────

interface ErrorBannerProps {
    message?: string;
    children?: ReactNode;
}

/**
 * DS: Red inline error banner — border-2 border-red-200 with alert icon.
 *
 * @example
 * {error && <ErrorBanner message="Failed to load personas. Please refresh." />}
 */
export function ErrorBanner({ message, children }: ErrorBannerProps) {
    return (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border-2 border-red-200 p-4 mb-6">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {message ?? children}
        </div>
    );
}

// ── LoadingState ──────────────────────────────────────────────────────────────

interface LoadingStateProps {
    label?: string;
    /** Fill the full viewport height instead of a fixed h-64 block */
    fullPage?: boolean;
}

/**
 * DS: Centered pulsing "LOADING..." text — matches the brutalist loading style.
 *
 * @example
 * {loading && <LoadingState />}
 * {loading && <LoadingState fullPage />}
 */
export function LoadingState({ label = 'Loading...', fullPage }: LoadingStateProps) {
    return (
        <div className={`flex items-center justify-center ${fullPage ? 'min-h-dvh' : 'h-64'}`}>
            <div className="text-2xl font-black uppercase animate-pulse">{label}</div>
        </div>
    );
}

// ── EmptyState ────────────────────────────────────────────────────────────────

interface EmptyStateProps {
    icon?: ReactNode;
    title: string;
    description?: string;
    action?: ReactNode;
}

/**
 * DS: Empty list placeholder — dashed border, centered icon + text + CTA.
 *
 * @example
 * <EmptyState
 *   icon={<Users className="w-16 h-16 text-gray-300" />}
 *   title="No Personas Yet"
 *   description="Create your first persona to get started"
 *   action={<Button variant="brutal" onClick={...}>New Persona</Button>}
 * />
 */
export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
    return (
        <div className="flex flex-col items-center justify-center h-64 bg-white border-2 border-dashed border-gray-300">
            {icon && <div className="mb-4 text-gray-300">{icon}</div>}
            <h2 className="text-2xl font-black uppercase text-gray-400 mb-2">{title}</h2>
            {description && <p className="text-sm text-gray-400 mb-4 text-center px-4">{description}</p>}
            {action}
        </div>
    );
}
